/**
 * RestrictedStatesPage
 *
 * Admin-only tab listing every state currently marked strict_isolated
 * (the "Restricted State" operating mode set from
 * /admin/statewide-details).
 *
 * Each card is now CLICKABLE — tapping expands an inline panel with:
 *   • Login URL      — copy-ready per-state signup link (/signup/CODE)
 *   • Create supervisor — inline form scoped to THIS state only
 *
 * Backend:
 *   GET  /api/twilio/admin/restricted-states
 *   POST /api/admin/restricted-states/{code}/supervisor
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Shield, Loader2, MapPin, Users as UsersIcon,
  ExternalLink, Copy, ChevronDown, ChevronRight, Link2, UserPlus, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

const niceStateName = (code) => {
  if (!code) return '';
  const up = String(code).toUpperCase();
  return STATE_NAMES[up] || up;
};

const RestrictedStateCard = ({ state, api, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '' });

  const code = (state.state || '').toUpperCase();
  const displayName = niceStateName(code);

  // Build the per-state login URL. The code may contain spaces or
  // hyphens (e.g. "CLIMATE ACTION DC", "EU-REGION-1") so we
  // URL-encode it — otherwise the raw-space link breaks the moment
  // an admin pastes it into email/Slack/SMS.
  const loginUrl = useMemo(() => {
    const origin = (typeof window !== 'undefined' && window.location.origin) || '';
    return `${origin}/signup/${encodeURIComponent(code)}`;
  }, [code]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(loginUrl);
      setCopied(true);
      toast.success('Login URL copied');
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Could not copy');
    }
  };

  const createSupervisor = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.first_name || !form.last_name) {
      toast.error('Fill every field');
      return;
    }
    setCreating(true);
    try {
      await api.post(`/admin/restricted-states/${encodeURIComponent(code)}/supervisor`, form);
      toast.success(`Supervisor created for ${displayName}`);
      setForm({ first_name: '', last_name: '', email: '', password: '' });
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create supervisor');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm border border-red-200/60 dark:border-red-900/40 overflow-hidden"
      data-testid={`restricted-state-${code}`}
    >
      {/* Clickable summary bar */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-2 p-3 text-left hover:bg-red-50/40 dark:hover:bg-red-900/10 transition-colors"
        data-testid={`restricted-state-toggle-${code}`}
      >
        <div className="flex items-start gap-2 min-w-0">
          {open ? (
            <ChevronDown className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          )}
          <div className="min-w-0">
            <p className="font-bold text-sm">
              {displayName}{' '}
              <span className="text-xs font-normal text-muted-foreground">({code})</span>
            </p>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
              {state.phone_number && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {state.phone_number}
                </span>
              )}
              <span className="flex items-center gap-1">
                <UsersIcon className="w-3 h-3" /> {state.user_count ?? 0} users
              </span>
              <span>{state.contact_count ?? 0} contacts</span>
            </div>
          </div>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 uppercase tracking-wider text-[10px] font-bold flex-shrink-0 mt-0.5">
          Restricted
        </span>
      </button>

      {open && (
        <div className="border-t border-red-200/60 dark:border-red-900/40 p-3 space-y-4 bg-red-50/30 dark:bg-red-900/5">
          {/* Login URL */}
          <section data-testid={`restricted-login-url-section-${code}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Link2 className="w-3.5 h-3.5 text-[#586D52]" />
              <p className="text-xs font-bold uppercase tracking-wider">Login URL</p>
            </div>
            <p className="text-[11px] text-muted-foreground mb-2">
              Share this link with new hires. Anyone who signs up here is automatically assigned to <strong>{displayName}</strong> and can only pick the Lead or Affiliate role.
            </p>
            <div className="flex gap-2">
              <Input
                readOnly
                value={loginUrl}
                onClick={(e) => e.currentTarget.select()}
                className="h-9 text-xs font-mono bg-white dark:bg-[#0d0d0d]"
                data-testid={`restricted-login-url-${code}`}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={copyUrl}
                className="h-9 flex-shrink-0"
                data-testid={`restricted-copy-url-${code}`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(loginUrl, '_blank', 'noopener')}
                className="h-9 flex-shrink-0"
                data-testid={`restricted-open-url-${code}`}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </div>
          </section>

          {/* Create Supervisor */}
          <section data-testid={`restricted-supervisor-section-${code}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <UserPlus className="w-3.5 h-3.5 text-[#586D52]" />
              <p className="text-xs font-bold uppercase tracking-wider">Create Supervisor for {displayName}</p>
            </div>
            <p className="text-[11px] text-muted-foreground mb-2">
              Supervisors created here are pinned to <strong>{displayName}</strong> only. They cannot be created through the public signup link.
            </p>
            <form onSubmit={createSupervisor} className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">First name</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="h-9 text-sm"
                    data-testid={`sup-first-${code}`}
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Last name</Label>
                  <Input
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="h-9 text-sm"
                    data-testid={`sup-last-${code}`}
                  />
                </div>
              </div>
              <div>
                <Label className="text-[11px]">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-9 text-sm"
                  data-testid={`sup-email-${code}`}
                />
              </div>
              <div>
                <Label className="text-[11px]">Password</Label>
                <Input
                  type="password"
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="h-9 text-sm"
                  data-testid={`sup-password-${code}`}
                />
              </div>
              <Button
                type="submit"
                size="sm"
                disabled={creating}
                className="bg-[#586D52] hover:bg-[#4a5d46] h-9"
                data-testid={`sup-create-${code}`}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                {creating ? 'Creating…' : 'Create Supervisor'}
              </Button>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

export default function RestrictedStatesPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageHeader({ title: 'Restricted States' }, []);

  const load = async () => {
    try {
      const r = await api.get('/twilio/admin/restricted-states');
      setItems(r.data?.states || []);
    } catch (e) {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4"
      style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }}
      data-testid="restricted-states-page"
    >
      <p className="text-xs text-muted-foreground mb-4 max-w-prose">
        Users in a Restricted State can only see, chat, and schedule with other users{' '}
        <strong>inside the same state</strong>. Tap a state below to reveal its dedicated
        login URL and create supervisors for it. Configure the mode for each state under{' '}
        <button
          className="underline text-[#586D52]"
          onClick={() => navigate('/admin/statewide-details')}
          data-testid="restricted-goto-statewide"
        >
          Statewide Details
        </button>.
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Shield className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-sm font-semibold">No Restricted States yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Flip a state to <em>Restricted</em> under Statewide Details to add it here.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => navigate('/admin/statewide-details')}
            data-testid="restricted-add-via-statewide"
          >
            Go to Statewide Details
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <RestrictedStateCard key={s.state} state={s} api={api} onRefresh={load} />
          ))}
        </div>
      )}
    </div>
  );
}
